import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import * as React from "react";
import { Modal } from "../components/Modal";
import { MembershipType } from "../organizations/OrganizationStore";
import * as UserStore from "./UserStore";

type DetailsTab = "metadata" | "memberships" | "access" | "entra";

interface UserDetailsModalProps {
    isOpen: boolean;
    user: any;
    resolvedMetadata: any;
    organizationId: string;
    canManageUserPermissions: boolean;
    delegatedAccess: UserStore.DelegatedAccessResponse;
    onClose: () => void;
    onShowRelated: (key: string, value: string) => void;
    onStartAddDelegation: () => void;
    onRemoveDelegation: (delegateeId: string) => void;
}

const EmptyState = ({ children }) => <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">{children}</div>;

const MetadataPanel = ({ user, resolvedMetadata, onShowRelated }) => {
    const [view, setView] = React.useState<"rows" | "json">("rows");
    const metadata = user?.metadata || [];

    return (
        <div className="space-y-4">
            <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setView("rows")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${view === "rows" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}>Source rows</button>
                <button type="button" onClick={() => setView("json")} className={`px-3 py-1.5 text-xs font-medium rounded-md ${view === "json" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}>Resolved JSON</button>
            </div>

            {view === "json" ? (
                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-auto bg-gray-50 rounded-lg p-4">{JSON.stringify(resolvedMetadata, null, 2)}</pre>
            ) : metadata.length === 0 ? (
                <EmptyState>No metadata is available for this user.</EmptyState>
            ) : (
                <div className="space-y-2">
                    {metadata.map((item, index) => {
                        const actualValue = item.enableOverride ? item.overrideValue : item.value;
                        return (
                            <div key={item.id || `${item.key}-${index}`} className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg p-3">
                                <div className="min-w-0 text-sm text-gray-800 break-words">
                                    <span className="font-medium">{item.key}:</span>
                                    {item.enableOverride && <span className="line-through text-gray-500 ml-2">{item.value}</span>}
                                    <span className={item.enableOverride ? "text-blue-600 ml-2" : "ml-2"}>{actualValue}</span>
                                </div>
                                {actualValue && (
                                    <button type="button" onClick={() => onShowRelated(item.key, actualValue)} className="flex-shrink-0 px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700">Show related</button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const MembershipsPanel = ({ user }) => {
    const memberships = user?.organizationMembership || [];
    const groups = user?.groups || [];

    return (
        <div className="grid gap-6 md:grid-cols-2">
            <section>
                <h4 className="text-base font-semibold text-gray-900 mb-3">Organization memberships</h4>
                <div className="space-y-2">
                    {memberships.length === 0 ? <EmptyState>No organization memberships.</EmptyState> : memberships.map((member, index) => (
                        <div key={`${member.organizationID}-${member.type}-${index}`} className="border border-gray-200 rounded-lg p-3">
                            <div className="flex justify-between gap-2">
                                <div className="font-medium text-gray-900">{member.organization?.name || member.organizationID}</div>
                                {!member.enabled && <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">Archived</span>}
                            </div>
                            <div className="text-sm text-gray-600">{MembershipType(member.type)}</div>
                            {(member.source || member.sourceID) && <div className="text-xs text-gray-500 mt-1">{member.source} {member.sourceID && `· ${member.sourceID}`}</div>}
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <h4 className="text-base font-semibold text-gray-900 mb-3">Group memberships</h4>
                <div className="space-y-2">
                    {groups.length === 0 ? <EmptyState>No group memberships.</EmptyState> : groups.map((group, index) => (
                        <div key={group.id || index} className="border border-gray-200 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{group.name}</div>
                            {group.groupCustomType?.name && <div className="text-sm text-gray-600 mt-1">{group.groupCustomType.name}</div>}
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
};

const DelegatedAccessPanel = ({ canManage, data, onAdd, onRemove }) => {
    if (!canManage) return <EmptyState>You do not have permission to manage delegated access.</EmptyState>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h4 className="text-base font-semibold text-gray-900">Delegated access</h4>
                <button type="button" onClick={onAdd} className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                    <FontAwesomeIcon icon={["fad", "plus"]} className="w-3 h-3 mr-1" /> Add delegation
                </button>
            </div>

            <section>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Access given to others</h5>
                <div className="space-y-2">
                    {data?.hasGivenAccessTo?.length ? data.hasGivenAccessTo.map((delegation) => (
                        <div key={delegation.delegateeID} className="flex items-center justify-between gap-3 bg-green-50 border border-green-200 rounded-lg p-3">
                            <div>
                                <div className="font-medium text-gray-900">{delegation.delegatee.displayName || `${delegation.delegatee.firstName} ${delegation.delegatee.lastName}`}</div>
                                <div className="text-sm text-gray-600">{delegation.delegatee.email}</div>
                                {delegation.delegationRules && <div className="text-xs text-gray-500 mt-1">Rules: {delegation.delegationRules}</div>}
                            </div>
                            <button type="button" onClick={() => onRemove(delegation.delegateeID)} className="text-red-600 hover:text-red-800" title="Remove delegation">
                                <FontAwesomeIcon icon={["fad", "trash"]} className="w-4 h-4" />
                            </button>
                        </div>
                    )) : <EmptyState>This user has not given access to anyone.</EmptyState>}
                </div>
            </section>

            <section>
                <h5 className="text-sm font-medium text-gray-700 mb-2">Access received from others</h5>
                <div className="space-y-2">
                    {data?.hasAccessOn?.length ? data.hasAccessOn.map((delegation) => (
                        <div key={delegation.delegatorID} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{delegation.delegator.displayName || `${delegation.delegator.firstName} ${delegation.delegator.lastName}`}</div>
                            <div className="text-sm text-gray-600">{delegation.delegator.email}</div>
                            {delegation.delegationRules && <div className="text-xs text-gray-500 mt-1">Rules: {delegation.delegationRules}</div>}
                        </div>
                    )) : <EmptyState>This user has not received access from anyone.</EmptyState>}
                </div>
            </section>
        </div>
    );
};

const EntraPanel = ({ user, organizationId }) => {
    const [status, setStatus] = React.useState(null as any);
    const [licenses, setLicenses] = React.useState(null as any);
    const [isChecking, setIsChecking] = React.useState(false);
    const [isLoadingLicenses, setIsLoadingLicenses] = React.useState(false);
    const [expandedSkus, setExpandedSkus] = React.useState<Set<string>>(new Set());

    React.useEffect(() => {
        setStatus(null);
        setLicenses(null);
        setExpandedSkus(new Set());
    }, [user?.id, organizationId]);

    const loadLicenses = (userIdOrUpn: string) => {
        setIsLoadingLicenses(true);
        axios.get(`/Azure/Licenses/${organizationId}/User/${encodeURIComponent(userIdOrUpn)}`)
            .then((response) => setLicenses(response.data))
            .catch((error) => setLicenses({ success: false, errorMessage: error.response?.data?.error || error.message }))
            .finally(() => setIsLoadingLicenses(false));
    };

    const checkStatus = () => {
        if (!user?.email) return;
        setIsChecking(true);
        setStatus(null);
        setLicenses(null);
        axios.get(`/Azure/CheckUserEmail/${organizationId}?email=${encodeURIComponent(user.email)}`)
            .then((response) => {
                setStatus(response.data);
                if (response.data.exists && response.data.userPrincipalName) loadLicenses(response.data.userPrincipalName);
            })
            .catch((error) => setStatus({ exists: false, errorMessage: error.response?.data?.error || error.message }))
            .finally(() => setIsChecking(false));
    };

    const toggleSku = (skuId: string) => setExpandedSkus((current) => {
        const next = new Set(current);
        next.has(skuId) ? next.delete(skuId) : next.add(skuId);
        return next;
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <h4 className="text-base font-semibold text-gray-900">Azure Entra ID</h4>
                    <p className="text-sm text-gray-500">Check {user?.email || "this user"} and inspect assigned licenses.</p>
                </div>
                <button type="button" onClick={checkStatus} disabled={isChecking || !user?.email} className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                    <FontAwesomeIcon icon={["fad", isChecking ? "spinner" : "search"] as any} className={`w-3 h-3 mr-1 ${isChecking ? "animate-spin" : ""}`} />
                    {isChecking ? "Checking…" : "Check status"}
                </button>
            </div>

            {status && (
                <div className={`rounded-lg border p-4 ${status.exists ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                    <div className={`font-medium ${status.exists ? "text-green-800" : "text-red-800"}`}>{status.exists ? "User found in Entra ID" : "User not found in Entra ID"}</div>
                    {status.exists && <div className="mt-2 space-y-1 text-sm text-gray-700">
                        {status.displayName && <div><b>Display name:</b> {status.displayName}</div>}
                        {status.userPrincipalName && <div><b>UPN:</b> {status.userPrincipalName}</div>}
                        <div><b>Account:</b> {status.accountEnabled ? "Enabled" : "Disabled"}</div>
                    </div>}
                    {status.errorMessage && <div className="mt-2 text-sm text-red-700">{status.errorMessage}</div>}
                </div>
            )}

            {isLoadingLicenses && <div className="text-sm text-gray-600"><FontAwesomeIcon icon={["fad", "spinner"]} className="w-4 h-4 mr-2 animate-spin" />Loading licenses…</div>}
            {!isLoadingLicenses && licenses && !licenses.success && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{licenses.errorMessage || "Failed to load license information."}</div>}
            {!isLoadingLicenses && licenses?.success && (
                <div className="space-y-2">
                    {licenses.licenseDetails?.length ? licenses.licenseDetails.map((license) => {
                        const plans = expandedSkus.has(license.skuId) ? license.servicePlans : license.servicePlans?.slice(0, 5);
                        return <div key={license.skuId} className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{license.skuPartNumber}</div>
                            <div className="text-xs text-gray-500">SKU ID: {license.skuId}</div>
                            {!!plans?.length && <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-purple-200">
                                {plans.map((plan, index) => <span key={`${plan.servicePlanName}-${index}`} className="px-2 py-0.5 rounded text-xs font-medium bg-white text-gray-700">{plan.servicePlanName}</span>)}
                                {license.servicePlans.length > 5 && <button type="button" onClick={() => toggleSku(license.skuId)} className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200">{expandedSkus.has(license.skuId) ? "Show less" : `+${license.servicePlans.length - 5} more`}</button>}
                            </div>}
                        </div>;
                    }) : <EmptyState>No licenses are assigned to this user.</EmptyState>}
                </div>
            )}
        </div>
    );
};

export const UserDetailsModal = ({ isOpen, user, resolvedMetadata, organizationId, canManageUserPermissions, delegatedAccess, onClose, onShowRelated, onStartAddDelegation, onRemoveDelegation }: UserDetailsModalProps) => {
    const [tab, setTab] = React.useState<DetailsTab>("metadata");
    React.useEffect(() => setTab("metadata"), [user?.id]);

    const tabs: { id: DetailsTab; label: string; icon: string }[] = [
        { id: "metadata", label: "Metadata", icon: "brackets-curly" },
        { id: "memberships", label: "Memberships", icon: "users" },
        { id: "access", label: "Delegated access", icon: "user-shield" },
        { id: "entra", label: "Entra ID", icon: "cloud" },
    ];

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`User details${user?.fullName ? ` · ${user.fullName}` : ""}`} icon={["fad", "info-circle"]} iconColor="text-blue-600" size="xl">
            <div className="border-b border-gray-200 px-6 pt-4">
                <nav className="flex gap-1 overflow-x-auto" aria-label="User details">
                    {tabs.map((item) => <button key={item.id} type="button" onClick={() => setTab(item.id)} className={`inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 ${tab === item.id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                        <FontAwesomeIcon icon={["fad", item.icon] as any} className="w-4 h-4 mr-1.5" />{item.label}
                    </button>)}
                </nav>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
                {tab === "metadata" && <MetadataPanel user={user} resolvedMetadata={resolvedMetadata} onShowRelated={onShowRelated} />}
                {tab === "memberships" && <MembershipsPanel user={user} />}
                {tab === "access" && <DelegatedAccessPanel canManage={canManageUserPermissions} data={delegatedAccess} onAdd={onStartAddDelegation} onRemove={onRemoveDelegation} />}
                {tab === "entra" && <EntraPanel user={user} organizationId={organizationId} />}
            </div>
        </Modal>
    );
};
